/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Grid, 
  Link, 
  Box, 
  CircularProgress,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Alert
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRecaptchaV3 } from '../components/CaptchaComponent';
import logger from '../utils/logger';

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccessful, setRegistrationSuccessful] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  
  const { register, isAuthenticated, error, loading, clearError } = useAuth();
  const { executeRecaptcha } = useRecaptchaV3();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !registrationSuccessful) {
      navigate('/');
    }
  }, [isAuthenticated, navigate, registrationSuccessful]);

  // Clear any previous errors when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);

  const validateForm = () => {
    let isValid = true;
    
    // Check if passwords match
    if (password !== confirmPassword) {
      setPasswordError(t('passwordsDoNotMatch'));
      isValid = false;
    } else if (password.length < 6) {
      // Check password length
      setPasswordError(t('passwordTooShort'));
      isValid = false;
    } else {
      setPasswordError('');
    }
    
    // Check if terms are accepted
    if (!acceptTerms) {
      setTermsError(t('mustAcceptTerms'));
      isValid = false;
    } else {
      setTermsError('');
    }
    
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Execute reCAPTCHA v3 for registration action
      const captchaToken = await executeRecaptcha('register');
      
      // Include captcha token in registration data
      await register(username, email, password, captchaToken || undefined);
      
      // Show success message instead of redirecting
      setRegistrationSuccessful(true);
      setRegisteredEmail(email);
    } catch (error: any) {
      logger.error('Registration error:', error);
      // The error is already set in the AuthContext, so we don't need to set it again
      // The error will be displayed in the UI through the error state from useAuth()
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (registrationSuccessful) {
    return (
      <Grid 
        container 
        justifyContent="center" 
        alignItems="center" 
        style={{ minHeight: 'calc(100vh - 140px)' }}
      >
        <Grid item xs={12} sm={8} md={6} lg={4}>
          <Paper elevation={3} style={{ padding: '2rem', maxWidth: '500px', margin: '0 auto' }}>
            <Typography variant="h4" component="h1" align="center" gutterBottom>
              {t('registrationSuccessful')}
            </Typography>
            
            <Alert severity="success" sx={{ mb: 3 }}>
              {t('accountCreatedSuccessfully')}
            </Alert>
            
            <Typography paragraph>
              {t('registrationVerificationEmailSent', { email: registeredEmail }).split('{email}')[0]}
              <strong>{registeredEmail}</strong>
              {t('registrationVerificationEmailSent', { email: registeredEmail }).split('{email}').slice(1).join('')}
            </Typography>
            
            <Typography paragraph color="text.secondary" variant="body2">
              {t('canLoginLimitedUntilVerified')}
            </Typography>
            
            <Typography paragraph color="text.secondary" variant="body2">
              {t('whileWaitingVerifyBrowse')}
            </Typography>
            
            <Box mt={3} textAlign="center">
              <Button
                variant="contained"
                color="primary"
                component={RouterLink}
                to="/"
                fullWidth
              >
                {t('continueToHome')}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid 
      container 
      justifyContent="center" 
      alignItems="center" 
      style={{ minHeight: 'calc(100vh - 140px)' }}
    >
      <Grid item xs={12} sm={8} md={6} lg={4}>
        <Paper elevation={3} style={{ padding: '2rem', maxWidth: '500px', margin: '0 auto' }}>
          <Typography variant="h4" component="h1" align="center" gutterBottom>
            {t('register')}
          </Typography>
          
          {error && (
            <Typography color="error" align="center" paragraph>
              {error}
            </Typography>
          )}
          
          <form onSubmit={handleSubmit}>
            <TextField
              label={t('username')}
              fullWidth
              margin="normal"
              variant="outlined"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              label={t('emailAddress')}
              type="email"
              fullWidth
              margin="normal"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              label={t('password')}
              type="password"
              fullWidth
              margin="normal"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              error={!!passwordError}
              helperText={passwordError}
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              label={t('confirmPassword')}
              type="password"
              fullWidth
              margin="normal"
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              error={!!passwordError}
              InputLabelProps={{ shrink: true }}
            />
            
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    {t('iAgreeToTerms')}{' '}
                    <Link component={RouterLink} to="/terms" target="_blank">
                      {t('termsOfService')}
                    </Link>
                  </Typography>
                }
              />
              {termsError && <FormHelperText error>{termsError}</FormHelperText>}
            </Box>
            
            {/* reCAPTCHA v3 runs invisibly on form submission */}

            {/* Hidden honeypot field */}
            <input 
              type="text" 
              name="website" 
              style={{ display: 'none' }} 
              tabIndex={-1} 
              autoComplete="off"
            />
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              style={{ marginTop: '1.5rem' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : t('register')}
            </Button>
          </form>
          
          <Box mt={3} textAlign="center">
            <Typography variant="body2">
              {t('alreadyHaveAccount')}{' '}
              <Link component={RouterLink} to="/login">
                {t('loginHere')}
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default RegisterPage; 